/**
 * Konfigurátor sikeres beküldés – Data Layer push a Google Tag Managernek.
 * A konverzió és az Enhanced Conversion a GTM-ben van beállítva; itt csak az
 * eseményt és a user_data-t pusholjuk a dataLayerre.
 *
 * Data Layer esemény: configurator_success
 * GTM: Custom Event trigger (configurator_success) → Google Ads Conversion tag +
 *      Enhanced Conversion user_data a Data Layer változókból.
 * @see https://support.google.com/tagmanager/answer/6103696
 * @see https://support.google.com/google-ads/answer/13258081
 */

import type { ConfiguratorFormValues } from "@/components/price-generator/ConfiguratorForm";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** E.164 formátum (csak számjegyek, + prefix; 9 számjegy esetén +421). */
function normalizePhone(phone: string, defaultCountryCode = "421"): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length === 9) return `+${defaultCountryCode}${digits}`;
  if (digits.length >= 10 && digits.length <= 15) return `+${digits}`;
  return `+${defaultCountryCode}${digits}`;
}

function splitName(name: string): { first_name: string; last_name: string } {
  const t = name.trim();
  const space = t.indexOf(" ");
  if (space <= 0) return { first_name: t, last_name: "" };
  return {
    first_name: t.slice(0, space).trim(),
    last_name: t.slice(space).trim(),
  };
}

/** Enhanced Conversion user_data a GTM Data Layer változóknak (email, phone_number, address). */
function buildUserData(values: ConfiguratorFormValues): Record<string, unknown> {
  const userData: Record<string, unknown> = {};

  if (values.email?.trim()) {
    userData.email = values.email.trim().toLowerCase();
  }

  if (values.phone?.trim()) {
    userData.phone_number = normalizePhone(values.phone.trim());
  }

  const hasAddress =
    values.address?.trim() ||
    values.city?.trim() ||
    values.country?.trim() ||
    values.name?.trim();
  if (hasAddress) {
    const { first_name, last_name } = splitName(values.name?.trim() ?? "");
    const address: Record<string, string> = {};
    if (first_name) address.first_name = first_name;
    if (last_name) address.last_name = last_name;
    if (values.address?.trim()) address.street = values.address.trim();
    if (values.city?.trim()) address.city = values.city.trim();
    if (values.country?.trim()) address.country = values.country.trim();
    if (Object.keys(address).length > 0) userData.address = address;
  }

  return userData;
}

/** GTM Custom Event neve – ezt kell a GTM triggerben használni. */
export const CONFIGURATOR_SUCCESS_EVENT = "configurator_success";

/** postMessage típus, hogy a szülő oldal (pl. WordPress) GTM-je felismerje és pusholhassa a dataLayerre. */
export const CONFIGURATOR_SUCCESS_POSTMESSAGE_TYPE = "TPT_CONFIGURATOR_SUCCESS";

/**
 * Konfigurátor sikeres beküldése: push a dataLayerre a GTM számára.
 * Ha az oldal iframe-ben van, a szülőnek is postMessage-t küldünk, hogy a szülő GTM-je is lássa.
 */
export function reportConfiguratorConversion(values: ConfiguratorFormValues): void {
  if (typeof window === "undefined") return;

  const userData = buildUserData(values);
  const payload = { event: CONFIGURATOR_SUCCESS_EVENT, user_data: userData };

  // 1) Lokális dataLayer (ha a konfigurátor oldalán van GTM – pl. embed közvetlenül megnyitva)
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload as Record<string, unknown>);

  // 2) Ha iframe-ben vagyunk, a szülő oldal GTM-je nem látja a fenti push-t – küldjük postMessage-szel
  try {
    if (window !== window.parent) {
      window.parent.postMessage(
        { type: CONFIGURATOR_SUCCESS_POSTMESSAGE_TYPE, ...payload },
        "*"
      );
    }
  } catch {
    // cross-origin parent pl. nem enged postMessage-t, ignoráljuk
  }
}
